<script setup lang="ts">
import type { AwTableColumn, AwDisplayDataItem } from "@asap/design";
import type { Color } from "@asap/design/colors";
import { dateFormat } from "@asap/shared/utils/date";
import type { CreditInsurerRequestResponse } from "@asap/shared/schemas/credit-insurance";
import { currencyFormat } from "~/utils/currency";
import { useCompanyCreditGuarantee } from "~/components/company/composables/useCompanyCreditGuarantee";
import { ND_REQUESTED_AMOUNT_EUR } from "~/utils/credit-insurer-coverage";

const props = defineProps<{ companyId: string }>();

const { t } = useI18n();

const {
  requests,
  totalRequests,
  isLoadingRequests,
  hasLoadedRequests,
  isLoadingCovers,
  isSyncingCovers,
  checkingId,
  search,
  pageSize,
  pageIndex,
  currentCoveredAmount,
  lastCoverUpdate,
  refresh,
  syncCovers,
  runCheck,
} = useCompanyCreditGuarantee(toRef(props, "companyId"));

const isRequestModalOpen = ref(false);

type StatusBadge = { label: string; color: Color; icon?: string };

// A request's own submission lifecycle — the coverage decision is shown on the cover, not here.
const requestStatusBadge = (status: CreditInsurerRequestResponse["status"]): StatusBadge => {
  switch (status) {
    case "pending":
      return { label: t("companies.creditGuarantee.status.pending"), color: "info" };
    case "processed":
      return { label: t("companies.creditGuarantee.status.processed"), color: "gray" };
    case "failed":
      return { label: t("companies.creditGuarantee.status.failed"), color: "critical", icon: "i-ph-seal-warning-fill" };
  }
};

// What the insurer answered: only known for the latest processed request of a cover. Older processed
// requests stay a neutral "Traité".
const historyStatusBadge = (request: CreditInsurerRequestResponse): StatusBadge => {
  if (request.status !== "processed") return requestStatusBadge(request.status);
  switch (request.coverStatus) {
    case "refused":
      return { label: t("companies.creditGuarantee.status.refused"), color: "critical" };
    case "cancelled":
      return { label: t("companies.creditGuarantee.status.cancelled"), color: "gray" };
    case "pending":
      return { label: t("companies.creditGuarantee.status.pending"), color: "info" };
    case "approved": {
      const requestedAmount = getRequestedAmount(request);
      const isPartial =
        requestedAmount !== undefined && request.coveredAmount !== null && request.coveredAmount < requestedAmount;
      return isPartial
        ? { label: t("companies.creditGuarantee.status.partiallyApproved"), color: "warning" }
        : { label: t("companies.creditGuarantee.status.approved"), color: "success" };
    }
    case null:
      return requestStatusBadge(request.status);
  }
};

const eventLabel = (event: CreditInsurerRequestResponse["event"]) => {
  if (event === null) return "-";
  return event === "initial"
    ? t("companies.creditGuarantee.event.initial")
    : t("companies.creditGuarantee.event.increase");
};

const isCoveredAmountUnavailable = (request: CreditInsurerRequestResponse) =>
  request.status === "processed" && request.coverStatus === null;

const coveredAmountItem = computed<AwDisplayDataItem>(() => ({
  value: currencyFormat(currentCoveredAmount.value, { options: { minimumFractionDigits: 2 } }),
  label: lastCoverUpdate.value
    ? t("companies.creditGuarantee.coveredAmount.labelWithDate", {
        date: dateFormat(lastCoverUpdate.value, { options: { dateStyle: "short" } }),
      })
    : t("companies.creditGuarantee.coveredAmount.label"),
}));

const typeLabel = (type: CreditInsurerRequestResponse["type"]) =>
  type === "nd_cover" ? t("companies.creditGuarantee.type.nd") : t("companies.creditGuarantee.type.named");

// An ND request has no explicit amount; it covers up to the ND threshold, so we display that.
const getRequestedAmount = (request: CreditInsurerRequestResponse) =>
  request.type === "nd_cover" ? ND_REQUESTED_AMOUNT_EUR : (request.requestedAmount ?? undefined);

const requestColumns: AwTableColumn<CreditInsurerRequestResponse>[] = [
  { id: "creation", accessorKey: "createdAt", header: t("companies.creditGuarantee.columns.creation"), minSize: 120 },
  { id: "status", accessorKey: "status", header: t("companies.creditGuarantee.columns.status"), minSize: 220 },
  { id: "origin", accessorKey: "createdByName", header: t("companies.creditGuarantee.columns.origin"), minSize: 160 },
  { id: "type", accessorKey: "type", header: t("companies.creditGuarantee.columns.type"), minSize: 90 },
  { id: "event", accessorKey: "event", header: t("companies.creditGuarantee.columns.event"), minSize: 200 },
  {
    id: "requested",
    accessorKey: "requestedAmount",
    header: t("companies.creditGuarantee.columns.requestedAmount"),
    minSize: 160,
  },
  {
    id: "covered",
    accessorKey: "coveredAmount",
    header: t("companies.creditGuarantee.columns.coveredAmount"),
    minSize: 160,
  },
  { id: "actions", header: "", size: 130 },
];

const ui = {
  actionRow: "flex flex-row items-center gap-3",
  cellText: "text-secondary-900",
  cellTextBold: "font-semibold text-secondary-900",
  cellWithAvatar: "flex flex-row items-center gap-1.5 text-secondary-900",
  emptyStateIcon: "w-12 h-12 text-gray-700 mb-4",
};
</script>

<template>
  <div class="flex flex-col gap-4 w-full min-w-0 min-h-0 overflow-auto">
    <AwSkeleton v-if="isLoadingCovers" class="w-full h-18" />
    <AwDisplayData v-else :items="[coveredAmountItem]" />

    <div class="flex flex-row items-center justify-between gap-2">
      <div :class="ui.actionRow">
        <h3 class="font-500 text-md">{{ $t("companies.creditGuarantee.title") }}</h3>
      </div>
      <div :class="ui.actionRow">
        <AwButton
          :label="$t('companies.creditGuarantee.actions.sync')"
          icon="i-ph-arrows-clockwise"
          variant="outline"
          color="secondary"
          :is-loading="isSyncingCovers"
          :disabled="isSyncingCovers"
          @click="syncCovers"
        />
        <AwButton
          :label="$t('companies.creditGuarantee.actions.requestCover')"
          icon="i-ph-money"
          variant="solid"
          color="secondary"
          @click="isRequestModalOpen = true"
        />
        <AwTextField
          v-model="search"
          class="w-58"
          :placeholder="$t('companies.creditGuarantee.searchPlaceholder')"
          leading-icon="i-ph-magnifying-glass"
        />
      </div>
    </div>

    <CompanyRequestCoverModal
      :open="isRequestModalOpen"
      :company-id="companyId"
      :current-covered-amount="currentCoveredAmount"
      source="company_tab"
      @update:open="isRequestModalOpen = $event"
      @created="refresh"
    />

    <AwSkeleton v-if="!hasLoadedRequests" class="w-full h-40" />

    <AwEmptyState
      v-else-if="totalRequests === 0 && !search.trim()"
      :label="$t('companies.creditGuarantee.empty.label')"
      :description="$t('companies.creditGuarantee.empty.description')"
    >
      <template #illustration>
        <AwIcon name="i-ph-shield-check" :class="ui.emptyStateIcon" />
      </template>
    </AwEmptyState>

    <AwEmptyState
      v-else-if="totalRequests === 0"
      :label="$t('companies.creditGuarantee.noResult.label')"
      :description="$t('companies.creditGuarantee.noResult.description')"
    />

    <div v-else :class="{ 'opacity-60 transition-opacity': isLoadingRequests }">
      <TableInteractive :data="requests" :columns="requestColumns" :loading="false" :enable-sorting="false">
        <template #creation-cell="{ row }">
          <span :class="ui.cellText">{{
            dateFormat(row.original.createdAt, { options: { dateStyle: "medium" } })
          }}</span>
        </template>
        <template #status-cell="{ row }">
          <AwBadge :color="historyStatusBadge(row.original).color">
            <AwIcon
              v-if="historyStatusBadge(row.original).icon"
              :name="historyStatusBadge(row.original).icon ?? ''"
              class="size-3.5"
            />
            <span>{{ historyStatusBadge(row.original).label }}</span>
          </AwBadge>
        </template>
        <template #origin-cell="{ row }">
          <div v-if="row.original.createdByName" :class="ui.cellWithAvatar">
            <AwAvatar size="sm" :text="row.original.createdByName" />
            <AwTooltip :text="row.original.createdByName">
              <span class="truncate max-w-48">{{ row.original.createdByName }}</span>
            </AwTooltip>
          </div>
          <span v-else :class="ui.cellText">-</span>
        </template>
        <template #type-cell="{ row }">
          <span :class="ui.cellText">{{ typeLabel(row.original.type) }}</span>
        </template>
        <template #event-cell="{ row }">
          <span :class="ui.cellText">{{ eventLabel(row.original.event) }}</span>
        </template>
        <template #requested-cell="{ row }">
          <TableCellPrice :price="getRequestedAmount(row.original)" />
        </template>
        <template #covered-cell="{ row }">
          <AwTooltip
            v-if="isCoveredAmountUnavailable(row.original)"
            :text="$t('companies.creditGuarantee.coveredUnavailableTooltip')"
          >
            <span class="text-secondary-700">{{ $t("companies.creditGuarantee.coveredUnavailable") }}</span>
          </AwTooltip>
          <span v-else :class="ui.cellTextBold">{{
            row.original.coveredAmount === null ? "-" : currencyFormat(row.original.coveredAmount)
          }}</span>
        </template>
        <template #actions-cell="{ row }">
          <AwButton
            v-if="row.original.status === 'pending'"
            :label="
              checkingId === row.original.id
                ? $t('companies.creditGuarantee.actions.checking')
                : $t('companies.creditGuarantee.actions.check')
            "
            variant="outline"
            color="secondary"
            size="sm"
            icon="i-ph-arrows-clockwise"
            :disabled="checkingId !== null"
            @click="runCheck(row.original.id)"
          />
        </template>
        <template #footer>
          <TablePagination
            :total-items="totalRequests"
            :page-size="pageSize"
            :page-index="pageIndex"
            @update:page-size="pageSize = $event"
            @update:page-index="pageIndex = $event"
          />
        </template>
      </TableInteractive>
    </div>
  </div>
</template>
